import * as React from 'react';
import $ from 'jquery';
import * as Sentry from '@sentry/browser';
import { enableMapSet } from 'immer';
import { QueryKey } from 'react-query';

import { jaxPromise } from '~/api/core/legacy';
import { getAgents } from '~/api/routes/agents';

import AgentsClient from '~/state/Agents/client';

import { queryClient, querySubscription, render } from '~/theseus';

import AlreadyEnabledModal from '~/theseus/components/AlreadyEnabledModal';
import AuthModal from '~/theseus/components/AuthModal';
import DomainVerification from '~/theseus/components/DomainVerification';
import FBLoginModal from '~/theseus/components/LoginModal';
import MetaPixelsBlockedModal from '~/theseus/components/MetaPixelsBlockedModal';
import Navbar from '~/theseus/components/Navbar';
import TermsConditionsModal from '~/theseus/components/TermsConditions';

import { getIdentityInfo } from '~/theseus/containers/Auth/selectors';
import { AuthState } from '~/theseus/containers/Auth';
import { authService } from '~/theseus/containers/Auth/service';

import { serviceSubscription } from '~/theseus/hooks/useServiceSelector';

import { deleteCookie } from '~/utils';

/* Once the user information is retrieved by `login_new`, we can identify the
 * user in Sentry in order to have better error tracing
 */
serviceSubscription(authService, getIdentityInfo, ({ rgUser, userId }) => {
  if (rgUser && rgUser.rg_info && userId) {
    const displayName = `${rgUser.rg_info?.first_name} ${rgUser.rg_info?.last_name}`;

    Sentry.setUser({
      email: rgUser.rg_info?.email,
      id: rgUser.rg_info?.id.toString(),
      name: displayName,
    });
    // site_uuid/site_domain are tags so events can be searched by site
    Sentry.setTags({
      site_uuid: rgUser.site_uuid,
      site_domain: rgUser.site_domain,
      fb_user_id: userId?.toString(),
    });
  }
});

function triggerAnalyze(link: string): void {
  $('.link__input').val(link);
  $('.link__container').trigger('submit');
}

async function getPost<TPost = unknown>(
  id: string,
  accountId: string,
  pageId: string,
): Promise<TPost | undefined> {
  try {
    const { post }: { post: TPost } = await jaxPromise('get_post', {
      id,
      account_id: accountId,
      page_id: pageId,
    });

    return post;
  } catch (e) {
    Sentry.captureException(e);
  }
}

async function analyzeLinkIfRepost(
  accountId: string,
  pageId: string,
  onRepost: (post: unknown) => void,
) {
  const { searchParams } = new URL(window.location.toString());
  const link = searchParams.get('link');
  const id = searchParams.get('id');

  if (link) {
    triggerAnalyze(link);
  } else if (id) {
    const post = await getPost(id, accountId, pageId);
    onRepost(post);
  }
}

/* Ensure that document is loaded so that we can ensure that we have access to
 * rgBar window object
 */
function docReady() {
  return new Promise((res) => {
    if (document.readyState !== 'loading') {
      res();
    } else {
      document.addEventListener('DOMContentLoaded', res);
    }
  });
}

/**
  * Subscribes to react-query queryKey and awaits for a value to be present
*/
async function waitForQueryDataToBeSet<T = unknown>(
  queryKey: QueryKey,
): Promise<T> {
  return new Promise((res) => {
    const unsubscribe = querySubscription<T>(queryKey, (data) => {
      if (data) {
        res(data);
        unsubscribe();
      }
    });
  });
}

/* Inject Facebook script into document and continue with login flow once the
 * Facebook SDK has loaded
 */
function loadFacebook() {
  return new Promise((res) => {
    const facebookScript = document.createElement('script');

    facebookScript.src = 'https://connect.facebook.net/en_US/sdk.js';
    facebookScript.async = true;
    facebookScript.defer = true;
    facebookScript.onload = res;

    document.body.appendChild(facebookScript);
  });
}

/**
 * Load and initialize Facebook SDK
 */
async function setupFacebook() {
  await loadFacebook();

  FB.init({
    appId: '1654788274772389',
    cookie: true,
    version: 'v22.0',
  });

  FB.AppEvents.logPageView();

  authService.send('FB.ready');
}

/**
 * Setup FB, rgBar and Sentry for the application. This should be
 * the first thing that is run in the application
 */
async function bootstrap() {
  await docReady();
  await setupFacebook();

  // Enable Map objects to be used with immer
  enableMapSet();

  /*
   * These are all of the potential google analytic cookies that could be present
   * in the window. We go ahead and remove all of them to prevent headaches later on
   *
   * This can eventually be removed without consequence
   */
  ['__utmzz'].map(deleteCookie('.realgeeks.com', '/'));

  Sentry.init({
    environment: process.env.NODE_ENV,
    dsn: process.env.SENTRY_DSN_URL,
    attachStacktrace: true,
  });
}

function ensureLoggedIn() {
  return new Promise<AuthState>((res) => {
    /* This onTransition will be triggered everytime our auth state machine
     * changes states. Once we know the user is logged in, we can continue our
     * flow and load page specific data
     */
    authService.onTransition((state) => {
      if (state.matches('loggedIn')) {
        res(state);
      }
    });
  });
}

/**
 * Entrypoint to every page in the application. This will ensure that the
 * Facebook SDK is loaded and setup as well as render any important auth
 * components. Each page must pass in their own custom onRepost function to
 * ensure that the link can be automatically analyzed and the ad values can be
 * prefilled
 */
export async function init(
  onRepost: (post: unknown) => void,
): Promise<{ pageId: string; accountId: string } | string> {
  await bootstrap();

  // We want to render these AFTER FB is initialized since these components
  // rely on FB
  render('#auth-modal', <AuthModal />);
  render('#fb-login-modal', <FBLoginModal />);
  render('#terms-conditions', <TermsConditionsModal />);
  render('#nav', <Navbar />);

  // Every page mounts this one, including /enable.html: a site whose state
  // doesn't allow tracking pixels can't use -- or enable -- this tool at all
  render('#meta-pixels-blocked-modal', <MetaPixelsBlockedModal />);

  if (
    window.location.pathname !== '/enable.html'
    && window.location.pathname !== '/'
  ) {
    render('#domain-verification', <DomainVerification />);
  }
  if (window.location.pathname === '/enable.html') {
    render('#already-enabled-modal', <AlreadyEnabledModal />);
  }

  const loggedInState = await ensureLoggedIn();

  if (window.location.pathname === '/enable.html') {
    return loggedInState.context.rgUser?.site_uuid as string;
  }

  // Retrieve agents but just absorb the error if anything goes wrong.
  // This might be a bad idea
  getAgents()
    .then(AgentsClient.agents.add)
    .catch(() => { });

  // Extract page_id from queries. If none exists yet, wait for one
  let pageId = queryClient.getQueryData('page_id') as string;
  if (!pageId) {
    pageId = await waitForQueryDataToBeSet<string>('page_id');
  }

  // Extract account_id from queries. If none exists yet, wait for one
  let accountId = queryClient.getQueryData('account_id') as string;
  if (!accountId) {
    accountId = await waitForQueryDataToBeSet<string>('account_id');
  }

  analyzeLinkIfRepost(accountId, pageId, onRepost);

  return { pageId, accountId };
}
